import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, onUnmounted, ref, watch } from "vue";
import AddSection from "./sections/AddSection.vue";
import SectionsList from "./sections/SectionsList.vue";
import EditBlock from "./sections/block/EditBlock.vue";
import { useActions, useGetters, useState } from "@/store/vuex-composition-mappers";
import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useRoute } from "vue-router";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";
import type { PageSection } from "@/apps/page/page.interfaces";
import { PAGE_SECTION_KINDS } from "@/apps/page/services/page.service";
import EditTool from "@/apps/page/project/sidebar/sections/tool/EditTool.vue";
import { useToolProjectsStore } from "@/apps/page/project/tool-projects.store";
import { storeToRefs } from "pinia";
import { useEditorActions } from "@/views/tools/page/utils/useEditorActions";
import { usePreviewActions } from "@/views/tools/page/utils/usePreviewActions";

// @ts-expect-error - TODO BF: Fix types

export default /*@__PURE__*/_defineComponent({
  __name: 'SidebarSections',
  setup(__props) {

const { removeBlockSection, changeBlockSection, setSidebarMode } = useActions("page", [
    "removeBlockSection",
    "changeBlockSection",
    "setSidebarMode",
]);
// @ts-expect-error - TODO BF: Fix types
const { saveSnapshot } = useActions("page/projects", ["saveSnapshot"]);
const { backupData, setBackupValue } = useBackupData<PageSection>({} as PageSection);
// @ts-expect-error - TODO BF: Fix types
const { getSectionById } = useGetters("page", ["getSectionById"]);
// @ts-expect-error - TODO BF: Fix types
const { sidebarMode } = useState("page", ["sidebarMode"]);

const SIDEBAR_MODES = {
    list: "list",
    addSection: "addSection",
    createBlock: "createBlock",
    editSection: "editSection",
    editBlock: "editBlock",
    editTool: "editTool",
} as const;

const mode = computed({
    get: () => sidebarMode.value,
    set: (value) => setSidebarMode(value),
});

const currentSection = ref<PageSection | null>(null);

const route = useRoute();
const { cleanQueryParams } = useCleanQueryParams();
const { createToolProject, publishToolProject } = useEditorActions();
const { scrollToSection } = usePreviewActions();

const edit = (section: PageSection, { triggerScrollTo } = { triggerScrollTo: true }) => {
    if (triggerScrollTo) {
        scrollToSection(section.webId);
    }
    if (section.kind === PAGE_SECTION_KINDS.TOOL && !section.projectId) {
        return createToolProject({ tool: section.type, sectionId: section.id });
    }
    if (
        section.kind === PAGE_SECTION_KINDS.TOOL &&
        section.projectId &&
        section.projectData?.access !== "published"
    ) {
        return publishToolProject({ tool: section.type, uuid: section.projectData?.uuid });
    }
    setBackupValue(section);
    currentSection.value = section;
    if (!currentSection.value.styleValues) {
        currentSection.value.styleValues = {};
    }
    mode.value = SIDEBAR_MODES.editBlock;
};

const toolProjectsStore = useToolProjectsStore();
const { toolProjects } = storeToRefs(toolProjectsStore);

watch(
    () => route.query.open,
    (value) => {
        switch (value) {
            case "addSection":
                mode.value = SIDEBAR_MODES.addSection;
                break;
            case "editBlock":
                if (!route.query.id) {
                    return;
                }

                edit(getSectionById.value(+route.query.id), { triggerScrollTo: false });
                setTimeout(() => {
                    cleanQueryParams(["open", "id"]);
                });
                break;
            case "editSection":
                if (!route.query.id) {
                    return;
                }

                edit(getSectionById.value(+route.query.id), { triggerScrollTo: false });
                setTimeout(() => {
                    cleanQueryParams(["open", "id"]);
                });
                break;
            case "createBlock":
                if (!route.query.id) {
                    return;
                }

                currentSection.value = getSectionById.value(+route.query.id);
                mode.value = SIDEBAR_MODES.createBlock;
                setTimeout(() => {
                    cleanQueryParams(
                        typeof route.query.id === "string" ? ["open", "id"] : ["open"]
                    );
                });
                break;
            default:
                break;
        }
    },
    { immediate: true }
);

const goBack = () => {
    mode.value = SIDEBAR_MODES.list;
    cleanQueryParams(["open"]);
};

const clearBlock = () => {
    currentSection.value = null;
    mode.value = SIDEBAR_MODES.list;
};

const saveBlock = async () => {
    clearBlock();
    await saveSnapshot();
};

const cancelBlock = async () => {
    if (mode.value === SIDEBAR_MODES.createBlock) {
        if (!currentSection.value) return;
        removeBlockSection(currentSection.value.id);
    } else if (mode.value === SIDEBAR_MODES.editBlock) {
        if (!backupData.value) return;
        changeBlockSection(backupData.value as PageSection);
    } else {
        throw new Error("Invalid mode");
    }

    clearBlock();
    await saveSnapshot();
};

onUnmounted(() => {
    mode.value = SIDEBAR_MODES.list;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (mode.value === SIDEBAR_MODES.list)
      ? (_openBlock(), _createBlock(SectionsList, {
          key: 0,
          onAddSection: _cache[0] || (_cache[0] = ($event: any) => (mode.value = SIDEBAR_MODES.addSection)),
          onEditSection: edit
        }))
      : _createCommentVNode("", true),
    (
            (mode.value === SIDEBAR_MODES.createBlock || mode.value === SIDEBAR_MODES.editBlock) &&
            currentSection.value !== null &&
            currentSection.value.kind === _unref(PAGE_SECTION_KINDS).BLOCK
        )
      ? (_openBlock(), _createBlock(EditBlock, {
          key: currentSection.value?.id,
          section: currentSection.value,
          onSaveBlock: saveBlock,
          onCancelBlock: cancelBlock
        }, null, 8, ["section"]))
      : _createCommentVNode("", true),
    (
            (mode.value === SIDEBAR_MODES.createBlock || mode.value === SIDEBAR_MODES.editBlock) &&
            currentSection.value !== null &&
            currentSection.value.kind === _unref(PAGE_SECTION_KINDS).TOOL
        )
      ? (_openBlock(), _createBlock(EditTool, {
          key: currentSection.value?.id,
          section: currentSection.value,
          backupData: _unref(backupData),
          onSave: saveBlock,
          onQuit: cancelBlock
        }, null, 8, ["section", "backupData"]))
      : _createCommentVNode("", true),
    _createVNode(AddSection, {
      class: _normalizeClass({ '-translate-x-[9999px]': mode.value !== SIDEBAR_MODES.addSection }),
      toolProjects: _unref(toolProjects) as Record<string, any>,
      onBack: goBack
    }, null, 8, ["class", "toolProjects"])
  ], 64))
}
}

})